function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

import PropTypes from "prop-types";

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */


import { useCallback, useEffect, useState } from 'react';
export function useInnerText(innerTextFallback) {
  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      ref = _useState2[0],
      setRef = _useState2[1];

  var _useState3 = useState(innerTextFallback),
      _useState4 = _slicedToArray(_useState3, 2),
      innerText = _useState4[0],
      setInnerText = _useState4[1];

  var updateInnerText = useCallback(function (node) {
    if (!node) return;
    setInnerText( // Check for `innerText` implementation rather than a simple OR check
    // because in real cases the result of `innerText` could correctly be `null`
    // while the result of `textContent` could correctly be non-`null` due to
    // differing reliance on browser layout calculations.
    // We prefer the result of `innerText`, if available.
    'innerText' in node ? node.innerText : node.textContent || innerTextFallback);
  }, [innerTextFallback]);
  useEffect(function () {
    var observer = new MutationObserver(function (mutationsList) {
      if (mutationsList.length) updateInnerText(ref);
    });

    if (ref) {
      updateInnerText(ref);
      observer.observe(ref, {
        characterData: true,
        subtree: true,
        childList: true
      });
    }

    return function () {
      observer.disconnect();
    };
  }, [ref, updateInnerText]);
  return [setRef, innerText];
}
export var EuiInnerText = ({
  children,
  fallback
}) => {
  const [ref, innerText] = useInnerText(fallback);
  return children(ref, innerText);
};
EuiInnerText.propTypes = {
  children: PropTypes.func.isRequired,
  fallback: PropTypes.string
};

try {
  // @ts-ignore
  useInnerText.displayName = "useInnerText"; // @ts-ignore

  useInnerText.__docgenInfo = {
    "description": "",
    "displayName": "useInnerText",
    "props": {
      "toString": {
        "defaultValue": null,
        "description": "Returns a string representation of a string.",
        "name": "toString",
        "required": false,
        "type": {
          "name": "() => string"
        }
      },
      "charAt": {
        "defaultValue": null,
        "description": "Returns the character at the specified index.\n@param pos The zero-based index of the desired character.",
        "name": "charAt",
        "required": true,
        "type": {
          "name": "(pos: number) => string"
        }
      },
      "charCodeAt": {
        "defaultValue": null,
        "description": "Returns the Unicode value of the character at the specified location.\n@param index The zero-based index of the desired character. If there is no character at the specified index, NaN is returned.",
        "name": "charCodeAt",
        "required": true,
        "type": {
          "name": "(index: number) => number"
        }
      },
      "concat": {
        "defaultValue": null,
        "description": "Returns a string that contains the concatenation of two or more strings.\n@param strings The strings to append to the end of the string.",
        "name": "concat",
        "required": true,
        "type": {
          "name": "(...strings: string[]) => string"
        }
      },
      "indexOf": {
        "defaultValue": null,
        "description": "Returns the position of the first occurrence of a substring.\n@param searchString The substring to search for in the string\n@param position The index at which to begin searching the String object. If omitted, search starts at the beginning of the string.",
        "name": "indexOf",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => number"
        }
      },
      "lastIndexOf": {
        "defaultValue": null,
        "description": "Returns the last occurrence of a substring in the string.\n@param searchString The substring to search for.\n@param position The index at which to begin searching. If omitted, the search begins at the end of the string.",
        "name": "lastIndexOf",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => number"
        }
      },
      "localeCompare": {
        "defaultValue": null,
        "description": "Determines whether two strings are equivalent in the current locale.\nDetermines whether two strings are equivalent in the current or specified locale.\n@param that String to compare to target string\n@param that String to compare to target string\n@param locales A locale string or array of locale strings that contain one or more language or locale tags. If you include more than one locale string, list them in descending order of priority so that the first entry is the preferred locale. If you omit this parameter, the default locale of the JavaScript runtime is used. This parameter must conform to BCP 47 standards; see the Intl.Collator object for details.\n@param options An object that contains one or more properties that specify comparison options. see the Intl.Collator object for details.",
        "name": "localeCompare",
        "required": true,
        "type": {
          "name": "{ (that: string): number; (that: string, locales?: string | string[] | undefined, options?: CollatorOptions | undefined): number; }"
        }
      },
      "match": {
        "defaultValue": null,
        "description": "Matches a string with a regular expression, and returns an array containing the results of that search.\nMatches a string an object that supports being matched against, and returns an array containing the results of that search.\n@param regexp A variable name or string literal containing the regular expression pattern and flags.\n@param matcher An object that supports being matched against.",
        "name": "match",
        "required": true,
        "type": {
          "name": "{ (regexp: string | RegExp): RegExpMatchArray | null; (matcher: { [Symbol.match](string: string): RegExpMatchArray | null; }): RegExpMatchArray | null; }"
        }
      },
      "replace": {
        "defaultValue": null,
        "description": "Replaces text in a string, using a regular expression or search string.\nReplaces text in a string, using a regular expression or search string.\nReplaces text in a string, using an object that supports replacement within a string.\nReplaces text in a string, using an object that supports replacement within a string.\n@param searchValue A string to search for.\n@param replaceValue A string containing the text to replace for every successful match of searchValue in this string.\n@param searchValue A string to search for.\n@param replacer A function that returns the replacement text.\n@param searchValue A object can search for and replace matches within a string.\n@param replaceValue A string containing the text to replace for every successful match of searchValue in this string.\n@param searchValue A object can search for and replace matches within a string.\n@param replacer A function that returns the replacement text.",
        "name": "replace",
        "required": true,
        "type": {
          "name": "{ (searchValue: string | RegExp, replaceValue: string): string; (searchValue: string | RegExp, replacer: (substring: string, ...args: any[]) => string): string; (searchValue: { ...; }, replaceValue: string): string; (searchValue: { ...; }, replacer: (substring: string, ...args: any[]) => string): string; }"
        }
      },
      "search": {
        "defaultValue": null,
        "description": "Finds the first substring match in a regular expression search.\nFinds the first substring match in a regular expression search.\n@param regexp The regular expression pattern and applicable flags.\n@param searcher An object which supports searching within a string.",
        "name": "search",
        "required": true,
        "type": {
          "name": "{ (regexp: string | RegExp): number; (searcher: { [Symbol.search](string: string): number; }): number; }"
        }
      },
      "slice": {
        "defaultValue": null,
        "description": "Returns a section of a string.\n@param start The index to the beginning of the specified portion of stringObj.\n@param end The index to the end of the specified portion of stringObj. The substring includes the characters up to, but not including, the character indicated by end.\rIf this value is not specified, the substring continues to the end of stringObj.",
        "name": "slice",
        "required": true,
        "type": {
          "name": "(start?: number | undefined, end?: number | undefined) => string"
        }
      },
      "split": {
        "defaultValue": null,
        "description": "Split a string into substrings using the specified separator and return them as an array.\nSplit a string into substrings using the specified separator and return them as an array.\n@param separator A string that identifies character or characters to use in separating the string. If omitted, a single-element array containing the entire string is returned.\n@param limit A value used to limit the number of elements returned in the array.\n@param splitter An object that can split a string.\n@param limit A value used to limit the number of elements returned in the array.",
        "name": "split",
        "required": true,
        "type": {
          "name": "{ (separator: string | RegExp, limit?: number | undefined): string[]; (splitter: { [Symbol.split](string: string, limit?: number | undefined): string[]; }, limit?: number | undefined): string[]; }"
        }
      },
      "substring": {
        "defaultValue": null,
        "description": "Returns the substring at the specified location within a String object.\n@param start The zero-based index number indicating the beginning of the substring.\n@param end Zero-based index number indicating the end of the substring. The substring includes the characters up to, but not including, the character indicated by end.\rIf end is omitted, the characters from start through the end of the original string are returned.",
        "name": "substring",
        "required": true,
        "type": {
          "name": "(start: number, end?: number | undefined) => string"
        }
      },
      "toLowerCase": {
        "defaultValue": null,
        "description": "Converts all the alphabetic characters in a string to lowercase.",
        "name": "toLowerCase",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "toLocaleLowerCase": {
        "defaultValue": null,
        "description": "Converts all alphabetic characters to lowercase, taking into account the host environment's current locale.",
        "name": "toLocaleLowerCase",
        "required": true,
        "type": {
          "name": "(locales?: string | string[] | undefined) => string"
        }
      },
      "toUpperCase": {
        "defaultValue": null,
        "description": "Converts all the alphabetic characters in a string to uppercase.",
        "name": "toUpperCase",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "toLocaleUpperCase": {
        "defaultValue": null,
        "description": "Returns a string where all alphabetic characters have been converted to uppercase, taking into account the host environment's current locale.",
        "name": "toLocaleUpperCase",
        "required": true,
        "type": {
          "name": "(locales?: string | string[] | undefined) => string"
        }
      },
      "trim": {
        "defaultValue": null,
        "description": "Removes the leading and trailing white space and line terminator characters from a string.",
        "name": "trim",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "length": {
        "defaultValue": null,
        "description": "Returns the length of a String object.",
        "name": "length",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "substr": {
        "defaultValue": null,
        "description": "Gets a substring beginning at the specified location and having the specified length.\n@param from The starting position of the desired substring. The index of the first character in the string is zero.\n@param length The number of characters to include in the returned substring.",
        "name": "substr",
        "required": true,
        "type": {
          "name": "(from: number, length?: number | undefined) => string"
        }
      },
      "valueOf": {
        "defaultValue": null,
        "description": "Returns the primitive value of the specified object.",
        "name": "valueOf",
        "required": false,
        "type": {
          "name": "() => string"
        }
      },
      "codePointAt": {
        "defaultValue": null,
        "description": "Returns a nonnegative integer Number less than 1114112 (0x110000) that is the code point\nvalue of the UTF-16 encoded code point starting at the string element at position pos in\r\nthe String resulting from converting this object to a String.\r\nIf there is no element at that position, the result is undefined.\r\nIf a valid UTF-16 surrogate pair does not begin at pos, the result is the code unit at pos.",
        "name": "codePointAt",
        "required": true,
        "type": {
          "name": "(pos: number) => number | undefined"
        }
      },
      "includes": {
        "defaultValue": null,
        "description": "Returns true if searchString appears as a substring of the result of converting this\nobject to a String, at one or more positions that are\r\ngreater than or equal to position; otherwise, returns false.\n@param searchString search string\n@param position If position is undefined, 0 is assumed, so as to search all of the String.",
        "name": "includes",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => boolean"
        }
      },
      "endsWith": {
        "defaultValue": null,
        "description": "Returns true if the sequence of elements of searchString converted to a String is the\nsame as the corresponding elements of this object (converted to a String) starting at\r\nendPosition \u2013 length(this). Otherwise returns false.",
        "name": "endsWith",
        "required": true,
        "type": {
          "name": "(searchString: string, endPosition?: number | undefined) => boolean"
        }
      },
      "normalize": {
        "defaultValue": null,
        "description": "Returns the String value result of normalizing the string into the normalization form\nnamed by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.\nReturns the String value result of normalizing the string into the normalization form\r\nnamed by form as specified in Unicode Standard Annex #15, Unicode Normalization Forms.\n@param form Applicable values: \"NFC\", \"NFD\", \"NFKC\", or \"NFKD\", If not specified default\ris \"NFC\"\n@param form Applicable values: \"NFC\", \"NFD\", \"NFKC\", or \"NFKD\", If not specified default\ris \"NFC\"",
        "name": "normalize",
        "required": true,
        "type": {
          "name": "{ (form: \"NFC\" | \"NFD\" | \"NFKC\" | \"NFKD\"): string; (form?: string | undefined): string; }"
        }
      },
      "repeat": {
        "defaultValue": null,
        "description": "Returns a String value that is made from count copies appended together. If count is 0,\nthe empty string is returned.\n@param count number of copies to append",
        "name": "repeat",
        "required": true,
        "type": {
          "name": "(count: number) => string"
        }
      },
      "startsWith": {
        "defaultValue": null,
        "description": "Returns true if the sequence of elements of searchString converted to a String is the\nsame as the corresponding elements of this object (converted to a String) starting at\r\nposition. Otherwise returns false.",
        "name": "startsWith",
        "required": true,
        "type": {
          "name": "(searchString: string, position?: number | undefined) => boolean"
        }
      },
      "anchor": {
        "defaultValue": null,
        "description": "Returns an <a> HTML anchor element and sets the name attribute to the text value\n@param name",
        "name": "anchor",
        "required": true,
        "type": {
          "name": "(name: string) => string"
        }
      },
      "big": {
        "defaultValue": null,
        "description": "Returns a <big> HTML element",
        "name": "big",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "blink": {
        "defaultValue": null,
        "description": "Returns a <blink> HTML element",
        "name": "blink",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "bold": {
        "defaultValue": null,
        "description": "Returns a <b> HTML element",
        "name": "bold",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "fixed": {
        "defaultValue": null,
        "description": "Returns a <tt> HTML element",
        "name": "fixed",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "fontcolor": {
        "defaultValue": null,
        "description": "Returns a <font> HTML element and sets the color attribute value",
        "name": "fontcolor",
        "required": true,
        "type": {
          "name": "(color: string) => string"
        }
      },
      "fontsize": {
        "defaultValue": null,
        "description": "Returns a <font> HTML element and sets the size attribute value\nReturns a <font> HTML element and sets the size attribute value",
        "name": "fontsize",
        "required": true,
        "type": {
          "name": "{ (size: number): string; (size: string): string; }"
        }
      },
      "italics": {
        "defaultValue": null,
        "description": "Returns an <i> HTML element",
        "name": "italics",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "link": {
        "defaultValue": null,
        "description": "Returns an <a> HTML element and sets the href attribute value",
        "name": "link",
        "required": true,
        "type": {
          "name": "(url: string) => string"
        }
      },
      "small": {
        "defaultValue": null,
        "description": "Returns a <small> HTML element",
        "name": "small",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "strike": {
        "defaultValue": null,
        "description": "Returns a <strike> HTML element",
        "name": "strike",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "sub": {
        "defaultValue": null,
        "description": "Returns a <sub> HTML element",
        "name": "sub",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "sup": {
        "defaultValue": null,
        "description": "Returns a <sup> HTML element",
        "name": "sup",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "__@iterator": {
        "defaultValue": null,
        "description": "Iterator",
        "name": "__@iterator",
        "required": true,
        "type": {
          "name": "() => IterableIterator<string>"
        }
      },
      "padStart": {
        "defaultValue": null,
        "description": "Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.\nThe padding is applied from the start (left) of the current string.\n@param maxLength The length of the resulting string once the current string has been padded.\rIf this parameter is smaller than the current string's length, the current string will be returned as it is.\n@param fillString The string to pad the current string with.\rIf this string is too long, it will be truncated and the left-most part will be applied.\rThe default value for this parameter is \" \" (U+0020).",
        "name": "padStart",
        "required": true,
        "type": {
          "name": "(maxLength: number, fillString?: string | undefined) => string"
        }
      },
      "padEnd": {
        "defaultValue": null,
        "description": "Pads the current string with a given string (possibly repeated) so that the resulting string reaches a given length.\nThe padding is applied from the end (right) of the current string.\n@param maxLength The length of the resulting string once the current string has been padded.\rIf this parameter is smaller than the current string's length, the current string will be returned as it is.\n@param fillString The string to pad the current string with.\rIf this string is too long, it will be truncated and the left-most part will be applied.\rThe default value for this parameter is \" \" (U+0020).",
        "name": "padEnd",
        "required": true,
        "type": {
          "name": "(maxLength: number, fillString?: string | undefined) => string"
        }
      },
      "trimEnd": {
        "defaultValue": null,
        "description": "Removes the trailing white space and line terminator characters from a string.",
        "name": "trimEnd",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "trimStart": {
        "defaultValue": null,
        "description": "Removes the leading white space and line terminator characters from a string.",
        "name": "trimStart",
        "required": true,
        "type": {
          "name": "() => string"
        }
      },
      "trimLeft": {
        "defaultValue": null,
        "description": "Removes the leading white space and line terminator characters from a string.\nRemoves whitespace from the left end of a string.",
        "name": "trimLeft",
        "required": true,
        "type": {
          "name": "{ (): string; (): string; }"
        }
      },
      "trimRight": {
        "defaultValue": null,
        "description": "Removes the trailing white space and line terminator characters from a string.\nRemoves whitespace from the right end of a string.",
        "name": "trimRight",
        "required": true,
        "type": {
          "name": "{ (): string; (): string; }"
        }
      },
      "matchAll": {
        "defaultValue": null,
        "description": "Matches a string with a regular expression, and returns an iterable of matches\ncontaining the results of that search.\n@param regexp A variable name or string literal containing the regular expression pattern and flags.",
        "name": "matchAll",
        "required": true,
        "type": {
          "name": "(regexp: RegExp) => IterableIterator<RegExpMatchArray>"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/inner_text/inner_text.tsx#useInnerText"] = {
      docgenInfo: useInnerText.__docgenInfo,
      name: "useInnerText",
      path: "src/components/inner_text/inner_text.tsx#useInnerText"
    };
} catch (__react_docgen_typescript_loader_error) {}

try {
  // @ts-ignore
  EuiInnerText.displayName = "EuiInnerText"; // @ts-ignore

  EuiInnerText.__docgenInfo = {
    "description": "",
    "displayName": "EuiInnerText",
    "props": {
      "fallback": {
        "defaultValue": null,
        "description": "",
        "name": "fallback",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/inner_text/inner_text.tsx#EuiInnerText"] = {
      docgenInfo: EuiInnerText.__docgenInfo,
      name: "EuiInnerText",
      path: "src/components/inner_text/inner_text.tsx#EuiInnerText"
    };
} catch (__react_docgen_typescript_loader_error) {}